import React from 'react';
import {
  Box,
  HStack,
  Icon,
  Switch,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react-native';
import FormField from '@/common/components/forms/FormField';
import { useTenantEventKinds } from '@/common/context/TenantSettingsStore';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import { filterVisibleEventKinds } from '@/common/utils/eventKindVisibility';
import { isEventKindSticky } from '../utils/eventKindSticky';

interface EventConfigFieldsProps {
  isReadOnly?: boolean;
  moduleId?: string;
  version?: string;
  taskId?: string | null;
}

/**
 * EventConfigFields
 * -----------------
 * Sub-form for event tasks. Shows event kind selector, a compact summary of
 * inherited slot parameters, an optional "Customize" toggle when the event kind
 * allows overrides, and unlock condition controls.
 */
const EventConfigFields: React.FC<EventConfigFieldsProps> = ({
  isReadOnly = true,
  moduleId,
  version,
  taskId: selectedTaskId,
}) => {
  const {
    control, setValue, getValues, watch,
  } = useFormContext();
  const { t } = useTranslation();
  const { eventKinds, getEventKindById } = useTenantEventKinds();
  const { flags } = useUserFeatures();
  const {
    tasks, module: moduleVersion, versionHistory,
  } = useLecturerModuleData(moduleId, version, selectedTaskId ?? undefined);
  const selectedTask = (Array.isArray(tasks) ? tasks : []).find((tk: { id: string }) => tk.id === selectedTaskId);
  const taskType = watch('type');
  const savedEventKindId = selectedTask?.eventConfig?.eventKindId;
  const isEventKindLocked = isEventKindSticky({
    savedEventKindId,
    moduleWorkflowState: moduleVersion?.state?.workflowState,
    versionHistory,
  });
  const eventKindReadOnly = isReadOnly || isEventKindLocked;

  const eventKindId = useWatch({ control, name: 'eventConfig.eventKindId' });
  const durationOverride = useWatch({ control, name: 'eventConfig.durationMinutesOverride' });
  const capacityOverride = useWatch({ control, name: 'eventConfig.capacityOverride' });
  const unlockType = useWatch({ control, name: 'eventConfig.unlockConditions.type' });
  const isProctoredMode = eventKindId === 'proctored_exam';
  const selectedKind = eventKindId ? getEventKindById(eventKindId) : null;
  const isOralExamKind = selectedKind?.kind === 'oral_exam';

  // Oral exams have no task-level Customize. allowCustomCapacity is for the
  // calendar slot form (1:N), not the module oral-exam task.
  const canCustomize = !isOralExamKind
    && !!(selectedKind?.allowCustomDuration || selectedKind?.allowCustomCapacity);
  const hasActiveOverrides = durationOverride != null || capacityOverride != null;
  const [customizeOpen, setCustomizeOpen] = React.useState(false);

  const previousTasks = React.useMemo(() => (
    (Array.isArray(tasks) ? tasks : [])
      .filter((tk: { sortIndex?: number; id: string }) => (tk.sortIndex || 0) < (selectedTask?.sortIndex || 0) && tk.id !== selectedTaskId)
      .map((tk: { title: string; id: string }) => ({ label: tk.title, value: tk.id })) || []
  ), [tasks, selectedTask?.sortIndex, selectedTaskId]);

  React.useEffect(() => {
    if (!selectedKind) {
      setValue('eventConfig.durationMinutesOverride', null, { shouldDirty: false, shouldValidate: true });
      setValue('eventConfig.capacityOverride', null, { shouldDirty: false, shouldValidate: true });
      setCustomizeOpen(false);
      return;
    }
    if (selectedKind.kind === 'oral_exam' || !selectedKind.allowCustomDuration) {
      setValue('eventConfig.durationMinutesOverride', null, { shouldDirty: false, shouldValidate: true });
    }
    if (selectedKind.kind === 'oral_exam' || !selectedKind.allowCustomCapacity) {
      setValue('eventConfig.capacityOverride', null, { shouldDirty: false, shouldValidate: true });
    }
  }, [selectedKind, setValue]);

  React.useEffect(() => {
    if (!canCustomize) {
      setCustomizeOpen(false);
      return;
    }
    if (hasActiveOverrides) {
      setCustomizeOpen(true);
    }
  }, [canCustomize, hasActiveOverrides]);

  React.useEffect(() => {
    if (isProctoredMode) {
      setValue('eventConfig.unlockConditions.type', 'all_previous', { shouldValidate: true, shouldDirty: false });
      setValue('eventConfig.unlockConditions.taskIds', [], { shouldValidate: false, shouldDirty: false });
      return;
    }
    const currentUnlockType = getValues('eventConfig.unlockConditions.type');
    if (!currentUnlockType) {
      setValue('eventConfig.unlockConditions.type', 'always', { shouldValidate: false, shouldDirty: false });
    }
  }, [isProctoredMode, setValue, getValues]);

  const handleCustomizeToggle = React.useCallback((enabled: boolean) => {
    setCustomizeOpen(enabled);
    if (!enabled) {
      setValue('eventConfig.durationMinutesOverride', null, { shouldDirty: true, shouldValidate: true });
      setValue('eventConfig.capacityOverride', null, { shouldDirty: true, shouldValidate: true });
    }
  }, [setValue]);

  const allowedEventKinds = React.useMemo(() => {
    const visibleEventKinds = filterVisibleEventKinds(eventKinds, flags, eventKindId);
    if (taskType !== 'oral_exam') {
      return visibleEventKinds;
    }
    return visibleEventKinds.filter((kind) => kind.kind === 'oral_exam');
  }, [eventKinds, eventKindId, flags, taskType]);

  const eventKindChoices = allowedEventKinds.map((k) => ({ label: k.label, value: k.id }));
  const unlockTypeChoices = [
    { label: t('lecturer.moduleEditor.taskEditor.eventConfig.unlockAlways'), value: 'always' },
    { label: t('lecturer.moduleEditor.taskEditor.eventConfig.unlockAllPrevious'), value: 'all_previous' },
  ];

  const summaryText = selectedKind
    ? t('lecturer.moduleEditor.taskEditor.eventConfig.kindSummary', {
      kindLabel: selectedKind.label,
      duration: selectedKind.durationMin,
      capacity: selectedKind.maxCapacity,
    })
    : '';

  const durationValidation = React.useMemo(() => ({
    validate: (value: string | number | null | undefined) => {
      if (!selectedKind?.allowCustomDuration || value === '' || value === null || value === undefined) return true;
      const n = Number(value);
      if (Number.isNaN(n)) return t('lecturer.moduleEditor.taskEditor.eventConfig.errors.mustBeNumber');
      const min = selectedKind.minDurationMin ?? 15;
      const max = selectedKind.maxDurationMin ?? 1000;
      if (n < min) return t('lecturer.moduleEditor.taskEditor.eventConfig.errors.durationMin', { min });
      if (n > max) return t('lecturer.moduleEditor.taskEditor.eventConfig.errors.durationMax', { max });
      return true;
    },
  }), [selectedKind, t]);

  const capacityValidation = React.useMemo(() => ({
    validate: (value: string | number | null | undefined) => {
      if (!selectedKind?.allowCustomCapacity || value === '' || value === null || value === undefined) return true;
      const n = Number(value);
      if (Number.isNaN(n)) return t('lecturer.moduleEditor.taskEditor.eventConfig.errors.mustBeNumber');
      const min = selectedKind.minCapacityOverride ?? 1;
      const max = selectedKind.maxCapacityOverride ?? 1000;
      if (n < min) return t('lecturer.moduleEditor.taskEditor.eventConfig.errors.capacityMin', { min });
      if (n > max) return t('lecturer.moduleEditor.taskEditor.eventConfig.errors.capacityMax', { max });
      return true;
    },
  }), [selectedKind, t]);

  return (
    <VStack space="lg" maxWidth={560}>
      <FormField
        name="eventConfig.eventKindId"
        type="select"
        label={t('lecturer.moduleEditor.taskEditor.eventConfig.eventKind')}
        choices={eventKindChoices}
        horizontal
        fieldWidth={260}
        readonly={eventKindReadOnly}
        labelSize="xs"
        fieldSize="xs"
        errorTextSize="2xs"
        rules={{ required: t('lecturer.moduleEditor.taskEditor.eventConfig.errors.eventKindRequired') }}
      />

      {!!isEventKindLocked && (
        <Box
          testID="event-kind-sticky-notice"
          bg="$bgSubtle"
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$md"
          px="$3"
          py="$2.5"
        >
          <HStack space="sm" alignItems="flex-start">
            <Icon as={Info} size="xs" color="$statusInfo" flexShrink={0} mt="$0.5" />
            <VStack space="xs" flex={1}>
              <Text size="xs" color="$textBody">
                {t('lecturer.moduleEditor.taskEditor.eventConfig.eventKindSticky.explanation')}
              </Text>
              <Text size="xs" color="$textSubtle">
                {t('lecturer.moduleEditor.taskEditor.eventConfig.eventKindSticky.recovery')}
              </Text>
            </VStack>
          </HStack>
        </Box>
      )}

      {!!selectedKind && (
        <HStack
          space="sm"
          alignItems="center"
          justifyContent="space-between"
          bg="$bgSubtle"
          borderWidth={1}
          borderColor="$borderLight"
          px="$3"
          py="$2.5"
          borderRadius="$md"
        >
          <HStack space="xs" alignItems="center" flex={1}>
            <Icon as={Info} size="xs" color="$textHint" flexShrink={0} />
            <Text size="xs" color="$textSubtle" flex={1}>
              {summaryText}
            </Text>
          </HStack>
          {canCustomize && (
            <HStack space="sm" alignItems="center" flexShrink={0}>
              <Text size="xs" color="$textSubtle">
                {t('lecturer.moduleEditor.taskEditor.eventConfig.customize')}
              </Text>
              <Switch
                size="sm"
                isDisabled={isReadOnly}
                value={customizeOpen}
                onToggle={handleCustomizeToggle}
              />
            </HStack>
          )}
        </HStack>
      )}

      {customizeOpen && !!selectedKind && (
        <Box pl="$4" pr="$2">
          <HStack space="lg" alignItems="flex-start">
            {!!selectedKind.allowCustomDuration && (
              <Box flex={1} maxWidth={200}>
                <FormField
                  name="eventConfig.durationMinutesOverride"
                  type="text"
                  label={t('lecturer.moduleEditor.taskEditor.eventConfig.durationLabel')}
                  placeholder={String(selectedKind.durationMin)}
                  readonly={isReadOnly}
                  labelSize="xs"
                  fieldSize="xs"
                  errorTextSize="2xs"
                  helperText={t('lecturer.moduleEditor.taskEditor.eventConfig.durationOverrideHelperCompact', {
                    defaultValue: selectedKind.durationMin,
                    min: selectedKind.minDurationMin ?? selectedKind.durationMin,
                    max: selectedKind.maxDurationMin ?? selectedKind.durationMin,
                  })}
                  rules={durationValidation}
                />
              </Box>
            )}
            {!!selectedKind.allowCustomCapacity && (
              <Box flex={1} maxWidth={200}>
                <FormField
                  name="eventConfig.capacityOverride"
                  type="text"
                  label={t('lecturer.moduleEditor.taskEditor.eventConfig.capacityLabel')}
                  placeholder={String(selectedKind.maxCapacity)}
                  readonly={isReadOnly}
                  labelSize="xs"
                  fieldSize="xs"
                  errorTextSize="2xs"
                  helperText={t('lecturer.moduleEditor.taskEditor.eventConfig.capacityOverrideHelperCompact', {
                    defaultValue: selectedKind.maxCapacity,
                    min: selectedKind.minCapacityOverride ?? selectedKind.maxCapacity,
                    max: selectedKind.maxCapacityOverride ?? selectedKind.maxCapacity,
                  })}
                  rules={capacityValidation}
                />
              </Box>
            )}
          </HStack>
        </Box>
      )}

      <FormField
        name="eventConfig.unlockConditions.type"
        type="select"
        label={t('lecturer.moduleEditor.taskEditor.eventConfig.unlockWhen')}
        choices={unlockTypeChoices}
        horizontal
        fieldWidth={260}
        readonly={isReadOnly || isProctoredMode}
        labelSize="xs"
        fieldSize="xs"
        errorTextSize="2xs"
        defaultValue="always"
        rules={{ required: t('lecturer.moduleEditor.taskEditor.eventConfig.errors.unlockRequired') }}
      />
      {!!isProctoredMode && (
        <Text size="xs" color="$textSubtle">
          {t('lecturer.moduleEditor.taskEditor.eventConfig.proctoredUnlockPolicy')}
        </Text>
      )}
      {unlockType === 'specific_tasks' && (
        <FormField
          name="eventConfig.unlockConditions.taskIds"
          type="select"
          label={t('lecturer.moduleEditor.taskEditor.eventConfig.requiredTasks')}
          choices={previousTasks}
          horizontal
          fieldWidth={260}
          readonly={isReadOnly}
          labelSize="xs"
          fieldSize="xs"
          errorTextSize="2xs"
        />
      )}
    </VStack>
  );
};

export default EventConfigFields;
